/**
 * LE TITRE DE L'ONGLET — un par écran, et la référence de la fiche ouverte.
 *
 * ── Ce qu'il répare (relevé du 27/08/2026, P0.2) ────────────────────────────
 *
 * Les 47 pages du module renvoyaient toutes `document.title === "BIO24 Alliance
 * — Qualité"`. Le titre d'onglet n'est pas de la décoration : c'est la seule
 * chose que le navigateur donne à lire dans quatre endroits où l'application
 * n'a pas la main —
 *
 *   · l'onglet lui-même, quand on en a douze d'ouverts ;
 *   · l'historique (Ctrl+H), qui devenait une colonne du même mot répété ;
 *   · les favoris, tous nommés pareil, donc tous à renommer à la main ;
 *   · la recherche d'onglets (Ctrl+Maj+A), qui cherche dans ce titre et ne
 *     pouvait donc rien distinguer.
 *
 * Une personne qui travaille sur trois non-conformités et deux audits ouverts
 * en parallèle — c'est le cas nominal d'une RQ — n'avait aucun moyen de
 * retrouver le bon onglet autrement qu'en cliquant sur chacun.
 *
 * ── D'où vient le libellé, et pourquoi pas d'une table de plus ──────────────
 *
 * De `FAMILLES_NAV`, qui nomme DÉJÀ chaque écran dans la barre latérale. Une
 * seconde table aurait fini par dire autre chose que le menu : l'écran se serait
 * appelé « Non-conformités » à gauche et « NC » dans l'onglet. C'est la même
 * raison qui a fait naître `navigation.ts` — la barre et la palette de recherche
 * répondaient déjà deux fois aux mêmes questions.
 *
 * Seule `doc` (la FICHE d'un document) n'a pas d'entrée de menu : elle partage
 * son adresse avec la liste, et hérite donc de son libellé. La table de
 * dérogations ci-dessous existe pour ce seul cas, et le contrôle plus bas
 * refuse qu'un écran adressable reste sans nom.
 *
 * ── L'ordre des morceaux ────────────────────────────────────────────────────
 *
 * Du plus précis au plus général : `BIO24-NC-2026-014 — Non-conformités — BIO24
 * Qualité`. Un onglet rétréci coupe la FIN du titre — mettre la marque en tête
 * aurait donné douze onglets affichant « BIO24 Qual… ».
 */
import type { GedView } from '@/lib/types';
import { FAMILLES_NAV } from '@/lib/navigation';
import { ADRESSES } from '@/lib/adresse';

/** Ce qui ferme tout titre. Court : il est le premier tronqué. */
export const SUFFIXE_TITRE = 'BIO24 Qualité';

/** Écrans sans entrée de menu — cf. l'en-tête. */
const HORS_MENU: Partial<Record<GedView, string>> = {
  doc: 'Documents',
};

/** Libellé par écran, dérivé UNE fois de la navigation. */
const LIBELLES: Partial<Record<GedView, string>> = (() => {
  const out: Partial<Record<GedView, string>> = { ...HORS_MENU };
  for (const famille of FAMILLES_NAV) {
    for (const entree of famille.entrees) out[entree.view] = entree.label;
  }
  return out;
})();

/** Le libellé d'un écran, ou `null` s'il n'en a pas. */
export function libelleEcran(vue: GedView): string | null {
  return LIBELLES[vue] || null;
}

/**
 * Le titre de l'onglet pour un écran, et la fiche ouverte s'il y en a une.
 *
 * Fonction PURE : elle ne lit ni le store ni le document, et se vérifie donc
 * sans navigateur (`backend/tests/`).
 */
export function titreDeLaPage(vue: GedView, reference?: string | null): string {
  const libelle = libelleEcran(vue);
  const ref = String(reference || '').trim();
  // Un écran sans libellé ne doit pas produire « undefined — BIO24 Qualité » :
  // on retombe sur la marque seule, qui est au moins vraie.
  if (!libelle) return ref ? `${ref} — ${SUFFIXE_TITRE}` : SUFFIXE_TITRE;
  if (!ref) return `${libelle} — ${SUFFIXE_TITRE}`;
  return `${ref} — ${libelle} — ${SUFFIXE_TITRE}`;
}

/**
 * Les écrans ADRESSABLES qui n'ont pas de libellé — doit rester vide.
 *
 * Exporté pour être vérifié par le contrôle automatique plutôt que constaté à
 * l'écran : un écran ajouté sans entrée de menu produirait un onglet nommé
 * « BIO24 Qualité », c'est-à-dire le défaut qu'on vient de corriger, et rien
 * ne le signalerait.
 */
export function ecransSansLibelle(): GedView[] {
  return (Object.keys(ADRESSES) as GedView[]).filter((v) => !libelleEcran(v));
}
